import React, { useState, useEffect } from 'react';
import CategoryFilter from './components/CategoryFilter';
import DocumentGrid from './components/DocumentGrid';
import PDFViewer from './components/PDFViewer';
import ChatInterface from './components/ChatInterface';

function KnowledgeBase() {
  const [documents, setDocuments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // Documents matching search from backend
  const [isSearching, setIsSearching] = useState(false);
  const [view, setView] = useState('grid'); // 'grid' or 'pdf'
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [documentTopics, setDocumentTopics] = useState({}); // Extracted topics for each PDF
  const [topicsLoading, setTopicsLoading] = useState(true);

  // Fetch documents on mount
  useEffect(() => {
    const fetchDocuments = async () => {
    try {
      const response = await fetch('/api/documents', { credentials: 'include' });
      const data = await response.json();

      // Transform real PDF documents into display format
      const allDocs = [];

      // Process each category
      Object.entries(data.documents || {}).forEach(([category, files]) => {
        files.forEach(file => {
          // Create a readable title from filename
          const title = file.name
            .replace('.pdf', '')
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          allDocs.push({
            name: file.name,
            filename: file.name, // Add filename field for topic lookup
            title: title,
            category: category,
            categoryName: getCategoryName(category),
            pages: 100, // Placeholder - would need PDF parsing to get real count
            author: extractAuthor(file.name, category),
            lastModified: Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
            views: Math.floor(Math.random() * 1000),
            tags: getTagsForCategory(category)
            // sections will come from documentTopics state
          });
        });
      });

      setDocuments(allDocs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      // Use mock data as fallback
      setDocuments(getMockDocuments());
    }
    };

    fetchDocuments();
  }, []);

  // Fetch topics for all documents
  useEffect(() => {
    if (documents.length === 0) return;

    async function fetchAllTopics() {
      setTopicsLoading(true);
      const topicsMap = {};

      // Get all unique filenames
      const filenames = [...new Set(documents.map(doc => doc.filename))];

      // Fetch topics for each document
      await Promise.all(
        filenames.map(async (filename) => {
          try {
            const response = await fetch(`/api/documents/${filename}/topics`, {
              credentials: 'include'
            });
            const data = await response.json();
            topicsMap[filename] = data.topics || [];
          } catch (error) {
            console.error(`Failed to fetch topics for ${filename}:`, error);
            topicsMap[filename] = []; // Empty array on error
          }
        })
      );

      // Update documents with actual page counts from topics
      setDocuments(prevDocs => prevDocs.map(doc => {
        const topics = topicsMap[doc.filename] || [];
        // Calculate page count from highest page number in topics
        const maxPage = topics.length > 0
          ? Math.max(...topics.map(t => t.page || 0).filter(p => p > 0))
          : 0;

        return {
          ...doc,
          pages: maxPage || 100 // Use actual max page or default to 100
        };
      }));

      setDocumentTopics(topicsMap);
      setTopicsLoading(false);
    }

    fetchAllTopics();
  }, [documents.length]); // Only re-run when document count changes

  // Debounced search effect - call backend for full-text search
  useEffect(() => {
    // Clear search results if query is empty
    if (!searchQuery || !searchQuery.trim()) {
      setSearchResults(null);
      setIsSearching(false);
      return;
    }

    // Debounce search (wait 500ms after user stops typing)
    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(`/api/search?query=${encodeURIComponent(searchQuery)}`, { credentials: 'include' });
        const data = await response.json();
        setSearchResults(data.documents || []);
        console.log(`Search for "${searchQuery}" found ${data.documents?.length || 0} documents`);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const getMockDocuments = () => [
    {
      name: 'constitution.pdf',
      title: 'Constitution',
      category: 'statutes',
      categoryName: 'Statutes & Laws',
      pages: 50,
      author: 'Government',
      lastModified: Date.now() - 5 * 24 * 60 * 60 * 1000,
      views: 342,
      tags: ['Constitution', 'Law'],
      sections: []
    }
  ];

  const extractAuthor = (filename, category) => {
    // Extract author/source based on document type
    if (category === 'case_law') return 'Court';
    if (category === 'statutes') return 'Legislature';
    if (category === 'regulations') return 'Agency';
    if (category === 'briefs') return 'Legal Counsel';
    if (category === 'contracts') return 'Parties';
    return 'Legal System';
  };

  const getTagsForCategory = (category) => {
    const tagMap = {
      'statutes': ['Statute', 'Law', 'Legislation'],
      'case_law': ['Case', 'Precedent', 'Ruling'],
      'briefs': ['Brief', 'Argument', 'Motion'],
      'contracts': ['Contract', 'Agreement', 'Legal'],
      'regulations': ['Regulation', 'Rule', 'Compliance']
    };
    return tagMap[category] || ['Legal'];
  };

  const getCategoryName = (category) => {
    const names = {
      'statutes': 'Statutes & Laws',
      'case_law': 'Case Law',
      'briefs': 'Legal Briefs',
      'contracts': 'Contracts',
      'regulations': 'Regulations'
    };
    return names[category] || 'Documents';
  };

  const handleSelectDocument = (document, page = 1) => {
    setSelectedDocument(document);
    setSelectedPage(page);
    setView('pdf');
  };

  const handleBackToGrid = () => {
    setView('grid');
    setSelectedDocument(null);
    setSelectedPage(1);
  };

  const handleViewPdfFromChat = (document, page = 1) => {
    handleSelectDocument(document, page);
  };

  return (
    <div className="h-full flex bg-galaxy-950">
      {/* Left: Category Filter - 280px fixed */}
      <div className="w-[280px] flex-shrink-0">
        <CategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          documents={documents}
        />
      </div>

      {/* Center: Document Grid or PDF Viewer - flexible */}
      <div className="flex-1 min-w-0">
        {view === 'grid' ? (
          <DocumentGrid
            documents={documents}
            category={selectedCategory}
            searchQuery={searchQuery}
            searchResults={searchResults}
            isSearching={isSearching}
            onSelectDocument={handleSelectDocument}
            topics={documentTopics}
            topicsLoading={topicsLoading}
          />
        ) : (
          <PDFViewer
            document={selectedDocument}
            page={selectedPage}
            onBack={handleBackToGrid}
          />
        )}
      </div>

      {/* Right: Chat Interface - collapsible */}
      <div className="flex-shrink-0">
        <ChatInterface
          selectedComponents={selectedComponents}
          onComponentsChange={setSelectedComponents}
          onViewPdf={handleViewPdfFromChat}
        />
      </div>
    </div>
  );
}

export default KnowledgeBase;
